import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Dialog,
  DialogContent,
  Tooltip,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Search as SearchIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTeams,
  deleteTeam,
  setSelectedTeam,
} from "../../../redux/Slice/Settings/settingsSlice";
import AddTeams from "./AddTeams";
import { clearSuccessMessage } from "../../../redux/Slice/Settings/addfunctionsSlice";
import ShowMembers from "./ShowMembers"; // Import the ShowMembers component
import EditTeam from "./EditTeam"; // Import the EditTeam component
import AuthView from "../../../utils/AuthView";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound, checkSubscriptionActive, isSubscriptionBlockedError } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

// Match TeamMembers table typography on md/lg only
const tableHeaderFontSx = { fontSize: { md: "0.875rem", lg: "0.9rem" } };
const tableBodyFontSx = { fontSize: { md: "0.875rem", lg: "0.875rem" } };

const Teams = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
  const { teams, loading, selectedTeam } = useSelector(
    (state) => state.settings
  );
  const TeamPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Team || {}
  );

  const { successMessage: createTeamSuccess } = useSelector(
    (state) => state.addFunctions
  );

  useEffect(() => {
    if (createTeamSuccess) {
      toast.success(createTeamSuccess);
      dispatch(clearSuccessMessage());
    }
  }, [createTeamSuccess, dispatch]);

  const [currentPage, setCurrentPage] = useState(1);
  const [teamsPerPage, setTeamsPerPage] = useState(10);
  const [openAddTeamModal, setOpenAddTeamModal] = useState(false); // New state for the modal
  const [openEditTeamModal, setOpenEditTeamModal] = useState(false); // New state for edit modal
  const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null); // Team to edit
  const [searchQuery, setSearchQuery] = useState(""); // Add search query state

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => prev + direction);
  };

  const handleRowsPerPageChange = (event) => {
    setTeamsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleRowClick = (team) => {
    dispatch(setSelectedTeam(team)); // Set the selected team in Redux
  };

  const handleBackToTeams = () => {
    dispatch(setSelectedTeam(null)); // Clear the selected team in Redux
  };

  const handleAddTeamClick = async () => {
    const { blocked, message } = await checkBeforeOutbound(getAuthToken, ['teamsCreated']);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    setOpenAddTeamModal(true);
  };

  const handleEditTeam = async (team) => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    setSelectedTeamForEdit(team);
    setOpenEditTeamModal(true);
  };

  const handleDeleteTeam = async (e, teamId) => {
    e.stopPropagation();
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    dispatch(deleteTeam(teamId))
      .unwrap()
      .then(() => {
        dispatch(fetchTeams());
        toast.success("Team deleted successfully.");
      })
      .catch((error) => {
        if (!isSubscriptionBlockedError(error)) {
          toast.error(typeof error === 'string' ? error : "Failed to delete team.");
        }
      });
  };

  const handleCloseEditModal = () => {
    setOpenEditTeamModal(false);
    setSelectedTeamForEdit(null);
  };

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchTeams());
  }, [dispatch]);

  // Filter teams based on search query
  const filteredTeams = teams.filter((team) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      team.teamName.toLowerCase().includes(searchLower) ||
      (team.description && team.description.toLowerCase().includes(searchLower))
    );
  });

  // If we have a selected team, show its members
  if (selectedTeam) {
    return <ShowMembers team={selectedTeam} onBack={handleBackToTeams} />;
  }

  // Check if user has permission to view teams
  if (TeamPermissions.View === false) {
    return (
      <Box
        sx={{
          padding: "20px",
          height: "100%",
          borderRadius: "8px",
          backgroundColor: "background.paper",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          display: "flex",
          flexDirection: "column",
          color: "text.primary",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", marginBottom: "2rem" }}
        >
          Teams
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            paddingLeft: "2rem",
          }}
        >
          <AuthView />
        </Box>
      </Box>
    );
  }

  // Pagination logic
  const indexOfLastTeam = currentPage * teamsPerPage;
  const indexOfFirstTeam = indexOfLastTeam - teamsPerPage;
  const currentTeams = filteredTeams.slice(indexOfFirstTeam, indexOfLastTeam);

  return (
    <Box
      sx={{
        padding: { xs: "12px", sm: "20px", md: "20px", lg: "20px", xl: "20px" },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
        color: "text.primary",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: { xs: "0.2rem", sm: "1rem", md: "2rem", lg: "2rem", xl: "2rem" },
          marginTop: { xs: "0rem", sm: 0, md: 0, lg: 0, xl: 0 },
          gap: { xs: 0, sm: 2, md: 2, lg: 2, xl: 2 },
          flexShrink: 0,
        }}
      >
        
        <TextField
          fullWidth
          placeholder="Search teams..."
          variant="outlined"
          size={isXs ? "small" : "medium"}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1); // Reset to first page when searching
          }}
          sx={{
            flex: 1,
            "& .MuiInputBase-input": {
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1rem", lg: "1rem", xl: "1rem" },
              padding: { xs: "8px 12px", sm: "16.5px 14px", md: "16.5px 14px", lg: "16.5px 14px", xl: "16.5px 14px" },
              color: "text.primary",
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1rem", lg: "1rem", xl: "1rem" },
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.23)" 
                  : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.5)" 
                  : "rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip
          title={
            !TeamPermissions.Add ? "You don't have permission to add teams" : ""
          }
        >
          <span>
            <Button
              variant="outlined"
              size={isXs ? "small" : "medium"}
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.25rem", lg: "1.25rem", xl: "1.25rem" } }} />}
              onClick={handleAddTeamClick}
              disabled={!TeamPermissions.Add}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                borderRadius: "0.8rem",
                padding: { xs: "4px 8px", sm: "0.5rem 1rem", md: "0.5rem 1rem", lg: "0.5rem 1rem", xl: "0.5rem 1rem" },
                fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                textTransform: "none",
                marginLeft: { xs: 1, sm: 2, md: 2, lg: 2, xl: 2 },
                "&:hover": {
                  backgroundColor: "rgba(14, 144, 70, 0.08)",
                  borderColor: "primary.main",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Add Team
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Modal for adding teams */}
      <Dialog
        open={openAddTeamModal}
        onClose={() => setOpenAddTeamModal(false)}
        aria-labelledby="add-team-dialog-title"
        keepMounted
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: (t) => ({
            backgroundColor: t.palette.background.paper,
            color: t.palette.text.primary,
            // MUI dark Paper adds a white gradient overlay on elevation — reads as gray; remove for solid #1E1E1E
            backgroundImage: "none",
            boxShadow:
              t.palette.mode === "dark"
                ? "0 12px 48px rgba(0, 0, 0, 0.65)"
                : "0 8px 24px rgba(0, 0, 0, 0.18)",
          }),
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AddTeams onClose={() => setOpenAddTeamModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Modal for editing teams */}
      <EditTeam
        open={openEditTeamModal}
        onClose={handleCloseEditModal}
        team={selectedTeamForEdit}
      />

      {/* Teams Table */}
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: { 
            xs: "none", 
            sm: "none", 
            md: "none", 
            lg: "calc(100vh - 280px)", 
            xl: "calc(100vh - 280px)" 
          },
          boxShadow: "none",
          backgroundColor: "background.paper",
          overflow: "auto",
          paddingBottom: 0,
          border: (theme) => theme.palette.mode === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.12)" 
            : "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "1rem",
          // Decrease scrollbar size for all screen sizes
          scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" }, // Firefox
          "&::-webkit-scrollbar": {
            width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Vertical scrollbar - very thin for md/lg/xl
            height: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Horizontal scrollbar - very thin for md/lg/xl
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555" : "#888",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#777" : "#555",
            },
          },
          "& .MuiTableCell-root": {
            borderBottom: (theme) => theme.palette.mode === "dark" 
              ? "1px solid rgba(255, 255, 255, 0.12)" 
              : "1px solid rgba(0, 0, 0, 0.12)",
            color: "text.primary",
            padding: { 
              xs: "8px 4px", 
              sm: "10px 6px", 
              md: "12px 8px", 
              lg: "16px 12px", 
              xl: "16px 12px" 
            },
          },
          "& .MuiTableCell-root:last-child": {
            padding: { 
              xs: "8px 8px", 
              sm: "10px 10px", 
              md: "12px 12px", 
              lg: "16px 16px", 
              xl: "16px 16px" 
            },
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "background.paper",
            color: "text.primary",
            fontWeight: "bold",
            ...tableHeaderFontSx,
            padding: { 
              xs: "8px 4px", 
              sm: "10px 6px", 
              md: "12px 8px", 
              lg: "16px 12px", 
              xl: "16px 12px" 
            },
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            ...tableBodyFontSx,
          },
          "& .MuiTableHead-root .MuiTableCell-root:last-child": {
            padding: { 
              xs: "8px 8px", 
              sm: "10px 10px", 
              md: "12px 12px", 
              lg: "16px 16px", 
              xl: "16px 16px" 
            },
          },
          "& .MuiTableRow-root": {
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" 
                ? "rgba(255, 255, 255, 0.08)" 
                : "rgba(0, 0, 0, 0.04)",
            },
          },
        }}
      >
        <Table 
          size="small" 
          sx={{ 
            "& .MuiTableCell-root": { 
              paddingBottom: 0,
            },
            "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Pending Tasks</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Show loading skeletons when loading
              Array.from(Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton
                      variant="text"
                      width="100%"
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton
                      variant="text"
                      width="100%"
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton
                      variant="text"
                      width="100%"
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={30}
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredTeams.length === 0 ? (
              // Show message when no teams found
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {searchQuery ? "No teams found matching your search" : "No teams found"}
                </TableCell>
              </TableRow>
            ) : (
              // Show actual team data
              currentTeams.map((team) => (
                <TableRow
                  key={team._id}
                  hover
                  onClick={() => handleRowClick(team)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <TableCell>{team.teamName}</TableCell>
                  <TableCell align="center">
                    {team.members?.length || 0}
                  </TableCell>
                  <TableCell align="center">
                    {team.AssignedQueue?.length || 0}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      <Tooltip
                        title={
                          !TeamPermissions.Edit
                            ? "You don't have permission to edit teams"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={!TeamPermissions.Edit}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTeam(team);
                            }}
                            sx={{
                              borderRadius: "0.8rem",
                              textTransform: "none",
                              "&.Mui-disabled": {
                                borderColor: "gray",
                                color: "gray",
                              },
                            }}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !TeamPermissions.Delete
                            ? "You don't have permission to delete teams"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={!TeamPermissions.Delete}
                            onClick={(e) => handleDeleteTeam(e, team._id)}
                            sx={{
                              borderRadius: "0.8rem",
                              textTransform: "none",
                              "&.Mui-disabled": {
                                borderColor: "gray",
                                color: "gray",
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: { xs: "0.5rem", sm: "0.5rem", md: "0.75rem", lg: "0.75rem", xl: "0.75rem" },
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
          }}
        >
          {indexOfFirstTeam + 1}-{Math.min(indexOfLastTeam, filteredTeams.length)} of{" "}
          {filteredTeams.length}
        </Typography>

        <FormControl
          size="small"
          sx={{
            "& .MuiInputBase-root": {
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
              minHeight: { xs: "30px", sm: "34px", md: "34px", lg: "34px", xl: "34px" },
              padding: { xs: "2px 8px", sm: "3px 10px", md: "2px 8px", lg: "2px 8px", xl: "2px 8px" },
            },
            minWidth: { xs: "110px", sm: "130px", md: "130px", lg: "130px", xl: "130px" },
          }}
        >
          <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" } }}>Rows per page</InputLabel>
          <Select
            value={teamsPerPage}
            onChange={handleRowsPerPageChange}
            label="Rows per page"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" } }}
          >
            <MenuItem value={5} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" } }}>5</MenuItem>
            <MenuItem value={10} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" } }}>10</MenuItem>
            <MenuItem value={20} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" } }}>20</MenuItem>
          </Select>
        </FormControl>

        <Box>
          <IconButton
            onClick={() => handlePageChange(-1)}
            disabled={currentPage === 1}
            size="small"
            sx={{
              padding: { xs: "2px", sm: "4px", md: "3px", lg: "3px", xl: "3px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1.1rem", lg: "1.1rem", xl: "1.1rem" },
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(1)}
            disabled={indexOfLastTeam >= filteredTeams.length}
            size="small"
            sx={{
              padding: { xs: "2px", sm: "4px", md: "3px", lg: "3px", xl: "3px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1.1rem", lg: "1.1rem", xl: "1.1rem" },
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>

    </Box>
  );
};

export default Teams;
